import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { EnvService } from '@hub/services/core/env-service';
import { trackMixpanel } from '@monorepo/client-common';
import {
  Account,
  EventTypes,
  PayoutSource,
  WithdrawRulesResponse,
} from '@monorepo/types';
import { usePayoutEligibility } from '../../api/queries';
import { useStore } from '../../helpers/use-store';
import { useRiseIdValidation } from './hooks';
import WithdrawFundsContent from './withdraw-funds-content';

const WithdrawFunds = ({
  source,
  sourceId,
  displayName,
  tsAccountId,
  className,
  eligibilityData,
  account,
}: {
  source: PayoutSource;
  sourceId?: string;
  displayName?: string;
  tsAccountId?: string;
  className?: string;
  eligibilityData?: WithdrawRulesResponse;
  account?: Account;
}) => {
  const {
    dataStore: { userStore, applicationConfigStore },
  } = useStore();

  const { data: fetchedEligibilityData, refetch: refetchEligibility } =
    usePayoutEligibility(source, sourceId || '', account);

  // Use Rise ID validation hook
  const { validateRiseId } = useRiseIdValidation();

  // Use provided eligibility data or fallback to fetched data
  const eligible = eligibilityData ||
    fetchedEligibilityData || {
      isValid: false,
      availableAmount: 0,
    };

  const amountSelection = source === PayoutSource.Account;
  const impersonated = userStore.isImpersonated;

  const validateWithDrawEligible = async (): Promise<WithdrawRulesResponse> => {
    const [eligibilityResult] = await Promise.all([
      refetchEligibility(),
      validateRiseId(),
    ]);
    return (
      eligibilityResult.data || {
        isValid: false,
        availableAmount: 0,
      }
    );
  };

  useEffect(() => {
    trackMixpanel({
      event: EventTypes.MAIL_OTP_SENT,
      properties: {
        email: userStore.currentUser.email,
        platform: EnvService.get().application,
        sourceId,
        displayName,
        eligible,
      },
    });
  }, [displayName, eligible, sourceId, userStore.currentUser.email]);

  return (
    <WithdrawFundsContent
      validateWithDrawEligible={validateWithDrawEligible}
      credits={eligible?.credits || 0}
      userPayoutPercentage={eligible?.userPayoutPercentage || 0}
      disabled={!eligible.isValid || impersonated}
      maxWithdrawal={eligible.availableAmount}
      reason={eligible.reason}
      showAmountSelection={amountSelection}
      source={source}
      sourceId={sourceId || ''}
      minWithdrawal={
        applicationConfigStore.applicationConfig.minimumWithdrawAmount
      }
      displayName={displayName || ''}
      tsAccountId={tsAccountId}
      className={className}
      account={account}
    />
  );
};

export default observer(WithdrawFunds);
